import React, { useEffect, useState, useContext } from "react";

import {
  Button,
  Grid,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { KeyRound, Webhook, Copy, Check, Eye, EyeOff } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { BACKEND_URL } from "../../config/env";
import HelpTooltipIcon from "../HelpTooltipIcon";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      padding: 12,
      overflowY: "auto",
      overflowX: "hidden",
      ...(theme.scrollbarStyles || {}),
    },

    sectionCard: {
      borderRadius: 12,
      border: isDark
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      padding: 16,
      marginBottom: 12,
      transition: "all 0.2s ease",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 4,
    },
    sectionIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 32,
      height: 32,
      borderRadius: 8,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#1a1a1a",
    },
    sectionDescription: {
      fontSize: 12,
      color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)",
      marginBottom: 14,
      lineHeight: 1.5,
    },

    field: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
      "& .MuiInputLabel-root": {
        color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: theme.palette.primary.main,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark
          ? "rgba(255,255,255,0.18)"
          : "rgba(0,0,0,0.18)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark
          ? "rgba(255,255,255,0.30)"
          : "rgba(0,0,0,0.30)",
      },
    },

    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      width: "100%",
    },
    fieldLabel: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#111b21",
      lineHeight: 1.2,
    },

    eyeBtn: {
      color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)",
      padding: 4,
      "&:hover": {
        color: theme.palette.primary.main,
        background: "transparent",
      },
    },

    copyBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 500,
      padding: "5px 10px",
      borderRadius: 8,
      whiteSpace: "nowrap",
      transition: "background 0.18s ease, color 0.18s ease, border-color 0.18s ease",
      userSelect: "none",
      lineHeight: 1.2,
      border: isDark
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(0,0,0,0.10)",
      marginRight: -4,
    },
    copyBtnIdle: {
      color: isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.70)",
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      "&:hover": {
        color: theme.palette.primary.main,
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        borderColor: isDark
          ? "rgba(255,255,255,0.18)"
          : "rgba(0,0,0,0.18)",
      },
    },
    copyBtnDone: {
      color: isDark ? "#34d399" : "#047857",
      background: isDark
        ? "rgba(52, 211, 153, 0.14)"
        : "rgba(16, 185, 129, 0.10)",
      borderColor: isDark
        ? "rgba(52, 211, 153, 0.35)"
        : "rgba(16, 185, 129, 0.30)",
    },
  };
});

const MetaIntegration = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [socialFacebookAppId, setSocialFacebookAppId] = useState("");
  const [socialFacebookAppSecret, setSocialFacebookAppSecret] = useState("");
  const [facebookVerifyToken, setFacebookVerifyToken] = useState("");
  const [wabaFacebookAppId, setWabaFacebookAppId] = useState("");
  const [wabaFacebookAppSecret, setWabaFacebookAppSecret] = useState("");
  const [wabaFacebookConfigId, setWabaFacebookConfigId] = useState("");
  const [wabaWhatsappBusinessToken, setWabaWhatsappBusinessToken] = useState("");
  const [wabaVerifyToken, setWabaVerifyToken] = useState("");
  const [loadingSocialCredentials, setLoadingSocialCredentials] = useState(false);
  const [loadingWabaCredentials, setLoadingWabaCredentials] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedWebhookUrl, setCopiedWebhookUrl] = useState(false);
  const [copiedWabaWebhookUrl, setCopiedWabaWebhookUrl] = useState(false);
  const [copiedWabaToken, setCopiedWabaToken] = useState(false);
  const [showSocialSecret, setShowSocialSecret] = useState(false);
  const [showWabaSecret, setShowWabaSecret] = useState(false);
  const [showWabaToken, setShowWabaToken] = useState(false);

  const webhookUrl = `${BACKEND_URL}/webhook`;
  const wabaWebhookUrl = `${BACKEND_URL}/waba/webhook`;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user?.companyId) return;
      const { data } = await api.get("/integrations/meta/credentials");
      if (cancelled) return;
      setSocialFacebookAppId(data?.social?.facebookAppId || "");
      setSocialFacebookAppSecret(data?.social?.facebookAppSecret || "");
      setWabaFacebookAppId(data?.waba?.facebookAppId || "");
      setWabaFacebookAppSecret(data?.waba?.facebookAppSecret || "");
      setWabaFacebookConfigId(data?.waba?.facebookConfigId || "");
      setWabaWhatsappBusinessToken(data?.waba?.whatsappBusinessToken || "");
      setWabaVerifyToken(data?.waba?.wabaVerifyToken || "");
      setFacebookVerifyToken(data?.social?.facebookVerifyToken || "");
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  const handleSaveSocialCredentials = async () => {
    setLoadingSocialCredentials(true);
    try {
      await api.post("/integrations/meta/social-credentials", {
        facebookAppId: socialFacebookAppId,
        facebookAppSecret: socialFacebookAppSecret,
        facebookVerifyToken
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingSocialCredentials(false);
    }
  };

  const handleSaveWabaCredentials = async () => {
    setLoadingWabaCredentials(true);
    try {
      await api.post("/integrations/meta/waba-credentials", {
        facebookAppId: wabaFacebookAppId,
        facebookAppSecret: wabaFacebookAppSecret,
        facebookConfigId: wabaFacebookConfigId,
        whatsappBusinessToken: wabaWhatsappBusinessToken,
        wabaVerifyToken
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingWabaCredentials(false);
    }
  };

  const renderCopyBtn = (value, copied, setCopied) => (
    <span
      className={`${classes.copyBtn} ${copied ? classes.copyBtnDone : classes.copyBtnIdle}`}
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? <Check size={12} strokeWidth={2.5} /> : <Copy size={12} strokeWidth={2} />}
      {copied ? i18n.t("settings.meta.copied") : i18n.t("settings.meta.copy")}
    </span>
  );

  const metaFieldHelp = (field, fallback) => {
    const key = `settings.meta.fieldHelps.${field}`;
    const value = i18n.t(key);
    return value === key ? fallback : value;
  };

  const FieldGroup = ({ label, field, fallback, children }) => (
    <div className={classes.fieldGroup}>
      <span className={classes.fieldLabel}>
        {label}
        <HelpTooltipIcon
          helpKey={`settings.meta.fieldHelpsDetailed.${field}`}
          description={metaFieldHelp(field, fallback)}
          size={13}
          placement="top"
        />
      </span>
      {children}
    </div>
  );

  const noAutoFillProps = {
    autoComplete: "new-password",
    inputProps: {
      autoComplete: "new-password",
      "data-lpignore": "true",
      "data-1p-ignore": "true",
      "data-form-type": "other"
    }
  };

  return (
    <div className={classes.container}>
      {/* WhatsApp Embedded Signup */}
      <div className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <span className={classes.sectionIconWrap}>
            <KeyRound size={18} strokeWidth={1.8} />
          </span>
          <Typography className={classes.sectionTitle}>
            WhatsApp API Oficial
          </Typography>
          <HelpTooltipIcon helpKey="settings.meta.helps.credentials" />
        </div>
        <Typography className={classes.sectionDescription}>
          App Meta exclusivo para cadastro incorporado e WhatsApp Cloud API. Use outro app aqui se quiser aprovar WhatsApp separado de Facebook/Instagram.
        </Typography>

        <Grid container spacing={2}>
          <Grid xs={12} sm={6} item>
            <FieldGroup
              label="Facebook App ID"
              field="wabaFacebookAppId"
              fallback="ID numérico do App Meta usado no cadastro incorporado do WhatsApp API Oficial."
            >
            <FormControl className={classes.field}>
              <TextField
                id="wabaFacebookAppId"
                name="meta_waba_app_id"
                size="small"
                variant="outlined"
                value={wabaFacebookAppId}
                onChange={(e) => setWabaFacebookAppId(e.target.value)}
                className={classes.field}
                {...noAutoFillProps}
              />
              <FormHelperText>App criado para WhatsApp API Oficial e cadastro incorporado.</FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>

          <Grid xs={12} sm={6} item>
            <FieldGroup
              label="Facebook App Secret"
              field="wabaFacebookAppSecret"
              fallback="Chave secreta do App Meta usado no WhatsApp API Oficial. Nunca compartilhe esse valor."
            >
            <FormControl className={classes.field}>
              <TextField
                id="wabaFacebookAppSecret"
                name="meta_waba_app_secret"
                size="small"
                variant="outlined"
                type={showWabaSecret ? "text" : "password"}
                value={wabaFacebookAppSecret}
                onChange={(e) => setWabaFacebookAppSecret(e.target.value)}
                className={classes.field}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      className={classes.eyeBtn}
                      onClick={() => setShowWabaSecret(!showWabaSecret)}
                      aria-label="toggle secret visibility"
                    >
                      {showWabaSecret ? (
                        <EyeOff size={16} strokeWidth={1.6} />
                      ) : (
                        <Eye size={16} strokeWidth={1.6} />
                      )}
                    </IconButton>
                  ),
                }}
                inputProps={noAutoFillProps.inputProps}
              />
              <FormHelperText>Chave secreta do App (nunca compartilhe).</FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>

          <Grid xs={12} sm={6} item>
            <FieldGroup
              label="Configuration ID (Embedded Signup)"
              field="wabaFacebookConfigId"
              fallback="ID da configuração do Facebook Login for Business usada para abrir o cadastro incorporado do WhatsApp."
            >
            <FormControl className={classes.field}>
              <TextField
                id="wabaFacebookConfigId"
                name="meta_waba_config_id"
                size="small"
                variant="outlined"
                placeholder="Ex: 123456789012345"
                value={wabaFacebookConfigId}
                onChange={(e) => setWabaFacebookConfigId(e.target.value)}
                className={classes.field}
                {...noAutoFillProps}
              />
              <FormHelperText>ID de configuração do Facebook Login for Business (usado no Embedded Signup do WhatsApp API)</FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>

          <Grid xs={12} sm={6} item>
            <FieldGroup
              label="WhatsApp System User Token"
              field="wabaWhatsappBusinessToken"
              fallback="Token permanente do usuário do sistema com permissões para gerenciar e enviar mensagens pela WhatsApp Cloud API."
            >
            <FormControl className={classes.field}>
              <TextField
                id="wabaWhatsappBusinessToken"
                name="meta_waba_system_user_token"
                size="small"
                variant="outlined"
                type={showWabaToken ? "text" : "password"}
                value={wabaWhatsappBusinessToken}
                onChange={(e) => setWabaWhatsappBusinessToken(e.target.value)}
                className={classes.field}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      className={classes.eyeBtn}
                      onClick={() => setShowWabaToken(!showWabaToken)}
                      aria-label="toggle token visibility"
                    >
                      {showWabaToken ? (
                        <EyeOff size={16} strokeWidth={1.6} />
                      ) : (
                        <Eye size={16} strokeWidth={1.6} />
                      )}
                    </IconButton>
                  ),
                }}
                inputProps={noAutoFillProps.inputProps}
              />
              <FormHelperText>System User Token com permissões whatsapp_business_management e whatsapp_business_messaging</FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>

        </Grid>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSaveWabaCredentials}
          disabled={
            loadingWabaCredentials ||
            !wabaFacebookAppId ||
            !wabaFacebookAppSecret ||
            !wabaFacebookConfigId ||
            !wabaWhatsappBusinessToken
          }
          style={{ marginTop: 12, textTransform: "none" }}
        >
          {loadingWabaCredentials
            ? i18n.t("settings.settingsConfig.options.updating")
            : "Salvar credenciais WhatsApp API Oficial"}
        </Button>
      </div>

      {/* Facebook / Instagram */}
      <div className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <span className={classes.sectionIconWrap}>
            <KeyRound size={18} strokeWidth={1.8} />
          </span>
          <Typography className={classes.sectionTitle}>
            Facebook e Instagram
          </Typography>
          <HelpTooltipIcon helpKey="settings.meta.helps.credentials" />
        </div>
        <Typography className={classes.sectionDescription}>
          App Meta exclusivo para Facebook Messenger e Instagram Direct. Use estes dados para submeter esse app separadamente à aprovação da Meta.
        </Typography>

        <Grid container spacing={2}>
          <Grid xs={12} sm={6} item>
            <FieldGroup
              label="Facebook App ID"
              field="socialFacebookAppId"
              fallback="ID numérico do App Meta usado para Facebook Messenger e Instagram Direct."
            >
            <FormControl className={classes.field}>
              <TextField
                id="socialFacebookAppId"
                name="meta_social_app_id"
                size="small"
                variant="outlined"
                value={socialFacebookAppId}
                onChange={(e) => setSocialFacebookAppId(e.target.value)}
                className={classes.field}
                {...noAutoFillProps}
              />
              <FormHelperText>App criado para Messenger e Instagram Direct.</FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>

          <Grid xs={12} sm={6} item>
            <FieldGroup
              label="Facebook App Secret"
              field="socialFacebookAppSecret"
              fallback="Chave secreta do App Meta usado para Facebook Messenger e Instagram Direct. Nunca compartilhe esse valor."
            >
            <FormControl className={classes.field}>
              <TextField
                id="socialFacebookAppSecret"
                name="meta_social_app_secret"
                size="small"
                variant="outlined"
                type={showSocialSecret ? "text" : "password"}
                value={socialFacebookAppSecret}
                onChange={(e) => setSocialFacebookAppSecret(e.target.value)}
                className={classes.field}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      className={classes.eyeBtn}
                      onClick={() => setShowSocialSecret(!showSocialSecret)}
                      aria-label="toggle secret visibility"
                    >
                      {showSocialSecret ? (
                        <EyeOff size={16} strokeWidth={1.6} />
                      ) : (
                        <Eye size={16} strokeWidth={1.6} />
                      )}
                    </IconButton>
                  ),
                }}
                inputProps={noAutoFillProps.inputProps}
              />
              <FormHelperText>Chave secreta do App (nunca compartilhe).</FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>
        </Grid>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSaveSocialCredentials}
          disabled={loadingSocialCredentials || !socialFacebookAppId || !socialFacebookAppSecret}
          style={{ marginTop: 12, textTransform: "none" }}
        >
          {loadingSocialCredentials
            ? i18n.t("settings.settingsConfig.options.updating")
            : "Salvar credenciais Facebook/Instagram"}
        </Button>
      </div>

      {/* Webhook */}
      <div className={classes.sectionCard}>
        <div className={classes.sectionHeader}>
          <span className={classes.sectionIconWrap}>
            <Webhook size={18} strokeWidth={1.8} />
          </span>
          <Typography className={classes.sectionTitle}>
            {i18n.t("settings.meta.webhook.title")}
          </Typography>
          <HelpTooltipIcon helpKey="settings.meta.helps.webhook" />
        </div>
        <Typography className={classes.sectionDescription}>
          {i18n.t("settings.meta.webhook.description")}
        </Typography>

        <Grid container spacing={2}>
          <Grid xs={12} sm={6} item>
            <FieldGroup
              label={i18n.t("settings.meta.webhook.verifyTokenLabel")}
              field="facebookVerifyToken"
              fallback="Token gerado pelo sistema para a Meta confirmar que o webhook pertence a esta instalação."
            >
            <FormControl className={classes.field}>
              <TextField
                id="facebookVerifyToken"
                name="meta_social_verify_token"
                size="small"
                variant="outlined"
                value={facebookVerifyToken}
                className={classes.field}
                autoComplete="off"
                InputProps={{
                  readOnly: true,
                  endAdornment: renderCopyBtn(
                    facebookVerifyToken,
                    copiedToken,
                    setCopiedToken
                  ),
                }}
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-form-type": "other"
                }}
              />
              <FormHelperText>
                {i18n.t("settings.meta.webhook.verifyTokenHelper")}
              </FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>

          <Grid xs={12} sm={6} item>
            <FieldGroup
              label={i18n.t("settings.meta.webhook.callbackUrlLabel")}
              field="webhookUrl"
              fallback="URL que deve ser cadastrada no webhook do App Meta de Facebook/Instagram."
            >
            <FormControl className={classes.field}>
              <TextField
                id="webhookUrl"
                name="meta_social_callback_url"
                size="small"
                variant="outlined"
                value={webhookUrl}
                className={classes.field}
                autoComplete="off"
                InputProps={{
                  readOnly: true,
                  endAdornment: renderCopyBtn(
                    webhookUrl,
                    copiedWebhookUrl,
                    setCopiedWebhookUrl
                  ),
                }}
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-form-type": "other"
                }}
              />
              <FormHelperText>
                {i18n.t("settings.meta.webhook.callbackUrlHelper")}
              </FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>

          <Grid xs={12} sm={6} item>
            <FieldGroup
              label="URL de Callback - WhatsApp Cloud API"
              field="wabaWebhookUrl"
              fallback="URL que deve ser cadastrada no webhook do produto WhatsApp do App Meta."
            >
            <FormControl className={classes.field}>
              <TextField
                id="wabaWebhookUrl"
                name="meta_waba_callback_url"
                size="small"
                variant="outlined"
                value={wabaWebhookUrl}
                className={classes.field}
                autoComplete="off"
                InputProps={{
                  readOnly: true,
                  endAdornment: renderCopyBtn(
                    wabaWebhookUrl,
                    copiedWabaWebhookUrl,
                    setCopiedWabaWebhookUrl
                  ),
                }}
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-form-type": "other"
                }}
              />
              <FormHelperText>
                Use este callback no produto WhatsApp do mesmo app Meta.
              </FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>

          <Grid xs={12} sm={6} item>
            <FieldGroup
              label="Verify Token WhatsApp Cloud API"
              field="wabaVerifyToken"
              fallback="Token gerado pelo sistema para validar o webhook do produto WhatsApp na Meta."
            >
            <FormControl className={classes.field}>
              <TextField
                id="wabaVerifyToken"
                name="meta_waba_verify_token"
                size="small"
                variant="outlined"
                value={wabaVerifyToken}
                className={classes.field}
                autoComplete="off"
                InputProps={{
                  readOnly: true,
                  endAdornment: renderCopyBtn(
                    wabaVerifyToken,
                    copiedWabaToken,
                    setCopiedWabaToken
                  ),
                }}
                inputProps={{
                  autoComplete: "off",
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-form-type": "other"
                }}
              />
              <FormHelperText>
                Use este token no webhook do produto WhatsApp na Meta.
              </FormHelperText>
            </FormControl>
            </FieldGroup>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default MetaIntegration;
